import React from 'react';
import PaywallOffer from './PaywallOffer';
import ScanProgress from './ScanProgress';
import YourSites from './YourSites';
import FirstAuditGuide from './FirstAuditGuide';
import { ApiError } from '../utils/api-error';
import { ScanProgress as ScanProgressType } from '../utils/api';
import { ScanResult } from '../utils/types';
import { T } from '../designTokens';

// ---------------------------------------------------------------------------
// Scanner homepage content below the audit band (issue #416): progress,
// the out-of-credits offer, errors, and the customer's sites (or, before
// their first audit, what the audit checks). Renders null when there is
// nothing to show, so the page never ends in a bare gray strip.
// ---------------------------------------------------------------------------

export default function ScannerDashboardContent({
  isLoading,
  progress,
  batchInfo,
  scanningUrl,
  creditError,
  error,
  claimScanId,
  pastScans,
  pastScansLoaded = true,
  authEnabled,
  isSignedIn,
  userId,
  packPrice,
  creditsPerPurchase,
  onViewScan,
  onAuditAgain,
}: {
  isLoading: boolean;
  progress: ScanProgressType | null;
  batchInfo: { current: number; total: number; url: string } | null;
  scanningUrl: string | null;
  creditError: ApiError | null;
  error: string | null;
  claimScanId: string | null | undefined;
  pastScans: ScanResult[];
  /** False until GET /api/scans has answered, so the first-run guide never flashes for a returning customer. */
  pastScansLoaded?: boolean;
  authEnabled: boolean;
  isSignedIn: boolean;
  userId?: string | null;
  /**
   * Pack pricing from useCreditInfo — absent until GET /api/credits answers
   * (#970). Threaded straight through to PaywallOffer, which omits the buy
   * control rather than quoting a guessed price.
   */
  packPrice?: number;
  creditsPerPurchase?: number;
  onViewScan: (scan: ScanResult) => void;
  /** Omitted when the account cannot run an audit (zero balance). */
  onAuditAgain?: (url: string) => void;
}) {
  const isWorkspace = !claimScanId && (!authEnabled || isSignedIn);
  const showPastScans = isWorkspace && pastScans.length > 0;
  const showFirstRun = isWorkspace && pastScansLoaded && pastScans.length === 0 && !isLoading && !creditError;

  if (!isLoading && !creditError && !error && !showPastScans && !showFirstRun) {
    return null;
  }

  return (
    <div className="dashboard-container scanner-home__body">
      {/* Progress Display */}
      {isLoading && progress && (
        <ScanProgress
          stage={progress.stage}
          message={progress.message}
          percentage={progress.percentage}
          url={batchInfo?.url ?? scanningUrl}
          batch={batchInfo ? { current: batchInfo.current, total: batchInfo.total } : null}
        />
      )}

      {/* Out-of-credits: an offer surface, not an error box (#943). The
          generic error banner below is unrelated — a joined-message string
          for non-credit scan failures, not an ApiErrorNotice render. */}
      {creditError && (
        <PaywallOffer
          state="out-of-credits"
          context="scan"
          userId={userId}
          packPrice={packPrice}
          creditsPerPurchase={creditsPerPurchase}
        />
      )}

      {/* Error Display */}
      {error && (
        <div
          role="alert"
          style={{
            backgroundColor: T.errorBg,
            border: `1px solid ${T.errorBorder}`,
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '32px',
            color: T.red700,
            fontSize: '15px',
            lineHeight: 1.5,
          }}
        >
          {error.includes('\n') ? (
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {error.split('\n').map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          ) : (
            error
          )}
        </div>
      )}

      {/* Loading State — only before the first progress frame arrives;
          once SSE events flow, ScanProgress above is the single source of truth */}
      {isLoading && !progress && (
        <p
          role="status"
          style={{
            margin: '0 0 40px',
            padding: '20px 24px',
            backgroundColor: T.white,
            border: `1px solid ${T.gray200}`,
            borderRadius: '10px',
            fontSize: '15px',
            lineHeight: 1.6,
            color: T.gray600,
          }}
        >
          <strong style={{ color: T.gray800, fontWeight: 600 }}>Starting the audit.</strong>{' '}
          {batchInfo && batchInfo.total > 1
            ? `Site ${batchInfo.current} of ${batchInfo.total}: ${batchInfo.url}`
            : 'Most audits finish in under 2 minutes, and the report opens here.'}
        </p>
      )}
      {showPastScans && (
        <YourSites scans={pastScans} onView={onViewScan} onAuditAgain={onAuditAgain} />
      )}
      {showFirstRun && <FirstAuditGuide />}
    </div>
  );
}
